import type {
	blogPostsBlockLayouts,
	footerSocialPlatforms,
	landingPageTextSizes,
	productHighlightProductTypes,
	productListTypes,
} from "../schemas/landing"
import type { ThinCourse } from "./course"
import type { ThinDownloadable } from "./downloadable"
import type { PublicThinEvent } from "./event"
import type { LandingPageBlock, LandingPageBlockOf } from "./landingBlock"
import type { LandingPageStyleId } from "./landingStyle"
import type { ThinPublicMembership } from "./membership"
import type { PublicPlatform } from "./platform"
import type { ThinPrivateSession } from "./privateSession"
import type { ThinVideo } from "./video"

export const landingPageFontIds = [
	"modern",
	"editorial",
	"friendly",
	"classic",
	"technical",
	"inter",
	"lato",
	"roboto",
	"poppins",
	"playfair-display",
] as const
export type LandingPageFontId = (typeof landingPageFontIds)[number]
export const landingPageAnimateInStyleIds = ["none", "fade", "slide-up", "scale-up"] as const
export type LandingPageAnimateInStyleId = (typeof landingPageAnimateInStyleIds)[number]
export const landingPageButtonStyleIds = ["pill", "rounded", "square", "soft", "outline", "bold"] as const
export type LandingPageButtonStyleId = (typeof landingPageButtonStyleIds)[number]

export type LandingPageTextSize = (typeof landingPageTextSizes)[number]

export const landingPageTextSizeLabels: Record<LandingPageTextSize, string> = {
	small: "Small",
	medium: "Medium",
	large: "Large",
	"extra-large": "Extra large",
}

export type FooterSocialPlatform = (typeof footerSocialPlatforms)[number]

export const footerSocialPlatformLabels: Record<FooterSocialPlatform, string> = {
	x: "X",
	facebook: "Facebook",
	instagram: "Instagram",
	tiktok: "TikTok",
	youtube: "YouTube",
	linkedin: "LinkedIn",
	substack: "Substack",
}

export const landingPageAnimateInStyleLabels: Record<LandingPageAnimateInStyleId, string> = {
	none: "None",
	fade: "Fade",
	"slide-up": "Slide up",
	"scale-up": "Scale up",
}

export const landingPageFonts: Record<
	LandingPageFontId,
	{ label: string; description: string; headingFamily: string; bodyFamily: string }
> = {
	modern: {
		label: "Modern Sans",
		description: "Crisp, neutral, and product-focused.",
		headingFamily: '"Manrope", ui-sans-serif, system-ui, sans-serif',
		bodyFamily: '"Source Sans 3", ui-sans-serif, system-ui, sans-serif',
	},
	editorial: {
		label: "Editorial",
		description: "Refined contrast for thought leaders and premium brands.",
		headingFamily: '"Playfair Display", Georgia, serif',
		bodyFamily: '"Source Sans 3", ui-sans-serif, system-ui, sans-serif',
	},
	friendly: {
		label: "Friendly",
		description: "Warm, approachable, and easy to scan.",
		headingFamily: '"Poppins", ui-sans-serif, system-ui, sans-serif',
		bodyFamily: '"Lora", Georgia, serif',
	},
	classic: {
		label: "Classic",
		description: "Trustworthy serif headings with calm body text.",
		headingFamily: '"Libre Baskerville", Georgia, serif',
		bodyFamily: '"Lato", ui-sans-serif, system-ui, sans-serif',
	},
	technical: {
		label: "Technical",
		description: "Precise and structured for expert-led academies.",
		headingFamily: '"IBM Plex Sans", ui-sans-serif, system-ui, sans-serif',
		bodyFamily: '"IBM Plex Sans", ui-sans-serif, system-ui, sans-serif',
	},
	inter: {
		label: "Inter",
		description: "Versatile, neutral, and highly readable.",
		headingFamily: '"Inter", ui-sans-serif, system-ui, sans-serif',
		bodyFamily: '"Inter", ui-sans-serif, system-ui, sans-serif',
	},
	lato: {
		label: "Lato",
		description: "Warm, balanced, and professional.",
		headingFamily: '"Lato", ui-sans-serif, system-ui, sans-serif',
		bodyFamily: '"Lato", ui-sans-serif, system-ui, sans-serif',
	},
	roboto: {
		label: "Roboto",
		description: "Clean, familiar, and easy to read.",
		headingFamily: '"Roboto", ui-sans-serif, system-ui, sans-serif',
		bodyFamily: '"Roboto", ui-sans-serif, system-ui, sans-serif',
	},
	poppins: {
		label: "Poppins",
		description: "Geometric, contemporary, and confident.",
		headingFamily: '"Poppins", ui-sans-serif, system-ui, sans-serif',
		bodyFamily: '"Poppins", ui-sans-serif, system-ui, sans-serif',
	},
	"playfair-display": {
		label: "Playfair Display",
		description: "Elegant, expressive, and editorial.",
		headingFamily: '"Playfair Display", Georgia, serif',
		bodyFamily: '"Playfair Display", Georgia, serif',
	},
}

// Every color is a CSS expression rather than a value, so a style reads the brand color from the theme
// variables it is rendered next to instead of having to be rebuilt whenever the creator changes it.
const solidButtonFill = {
	background: "var(--landing-primary)",
	foreground: "var(--landing-primary-foreground)",
	shadow: "0 10px 15px -3px color-mix(in srgb, var(--landing-primary), transparent 78%)",
	hoverBackground: "color-mix(in srgb, var(--landing-primary), black 8%)",
	hoverForeground: "var(--landing-primary-foreground)",
	hoverShadow: "0 20px 25px -5px color-mix(in srgb, var(--landing-primary), transparent 70%)",
	activeShadow: "0 10px 15px -3px color-mix(in srgb, var(--landing-primary), transparent 78%)",
	activeTransform: "scale(0.98)",
}

export const landingPageButtonStyles: Record<
	LandingPageButtonStyleId,
	{
		label: string
		description: string
		radius: string
		background: string
		foreground: string
		shadow: string
		hoverBackground: string
		hoverForeground: string
		hoverShadow: string
		activeShadow: string
		activeTransform: string
	}
> = {
	pill: {
		label: "Pill",
		description: "Fully rounded with a soft brand glow.",
		radius: "9999px",
		...solidButtonFill,
	},
	rounded: {
		label: "Rounded",
		description: "Gently rounded corners that suit most designs.",
		radius: "0.5rem",
		...solidButtonFill,
	},
	square: {
		label: "Square",
		description: "Sharp corners for a bold, editorial look.",
		radius: "0",
		...solidButtonFill,
	},
	soft: {
		label: "Soft",
		description: "A light brand tint instead of a solid fill.",
		radius: "0.75rem",
		background: "color-mix(in srgb, var(--landing-primary), white 86%)",
		foreground: "color-mix(in srgb, var(--landing-primary), black 45%)",
		shadow: "none",
		hoverBackground: "color-mix(in srgb, var(--landing-primary), white 76%)",
		hoverForeground: "color-mix(in srgb, var(--landing-primary), black 45%)",
		hoverShadow: "none",
		activeShadow: "none",
		activeTransform: "scale(0.98)",
	},
	outline: {
		label: "Outline",
		description: "A brand-colored outline that fills in on hover.",
		radius: "9999px",
		background: "transparent",
		foreground: "var(--landing-primary)",
		// An inset shadow rather than a border, so switching style never changes the size of a button.
		shadow: "inset 0 0 0 2px var(--landing-primary)",
		hoverBackground: "var(--landing-primary)",
		hoverForeground: "var(--landing-primary-foreground)",
		hoverShadow: "inset 0 0 0 2px var(--landing-primary)",
		activeShadow: "inset 0 0 0 2px var(--landing-primary)",
		activeTransform: "scale(0.98)",
	},
	bold: {
		label: "Bold",
		description: "Hard-edged with an offset drop shadow.",
		radius: "0.25rem",
		background: "var(--landing-primary)",
		foreground: "var(--landing-primary-foreground)",
		shadow: "4px 4px 0 0 color-mix(in srgb, var(--landing-primary), black 45%)",
		hoverBackground: "var(--landing-primary)",
		hoverForeground: "var(--landing-primary-foreground)",
		hoverShadow: "6px 6px 0 0 color-mix(in srgb, var(--landing-primary), black 45%)",
		// The offset shadow is the button's depth, so pressing it sinks the button towards the shadow
		// instead of scaling it the way every other style does.
		activeShadow: "2px 2px 0 0 color-mix(in srgb, var(--landing-primary), black 45%)",
		activeTransform: "translate(2px, 2px)",
	},
}

export type LandingPageSettings = {
	styleId: LandingPageStyleId
	/** Whether a custom academy brand color replaces the style's own accent. */
	usesBrandColor: boolean
	fontId: LandingPageFontId
	animateInStyle: LandingPageAnimateInStyleId
	buttonStyleId: LandingPageButtonStyleId
	isPasswordProtected: boolean
}

export type LandingPageSitePage = {
	id: number
	path: string
	title: string
	description: string | null
	sortOrder: number
	isHiddenInNavigation: boolean
	isHiddenInHeaderNavigation: boolean
	isHiddenInFooterNavigation: boolean
	navigationGroup: string | null
}

export type LandingPageNavigationPage = {
	id: number | string
	path: string
	title: string | null
	navigationGroup: string | null
	isExternal?: boolean
}

export type LandingPageExternalNavigationLink = {
	id: number
	title: string
	url: string
	sortOrder: number
	navigationGroup: string | null
}

export type LandingPageNavigationOrderEntry = { type: "page"; id: number } | { type: "externalLink"; id: number }

export function getNextLandingPageNavigationSortOrder(sortOrders: number[]): number {
	return Math.max(-1, ...sortOrders) + 1
}

export type LandingPageNavigationItem =
	| { type: "page"; page: LandingPageNavigationPage }
	| { type: "group"; label: string; pages: LandingPageNavigationPage[] }

export type LandingPageLinkOption = { path: string; label: string }
export type LandingPageLinkOptionGroup = { label: string; options: LandingPageLinkOption[] }
export type LandingPageLinkOptions = {
	academyUrl: string
	sitePages: LandingPageLinkOption[]
	academyPages: LandingPageLinkOptionGroup[]
}

export type ThinLandingPageSite = {
	settings: LandingPageSettings
	blocks: LandingPageBlock[]
	page: {
		id: number
		path: string
		title: string
		description: string | null
		sortOrder: number
	}
	courses: ThinCourse[]
	videos: ThinVideo[]
	downloadables: ThinDownloadable[]
	sessions: ThinPrivateSession[]
	events: PublicThinEvent[]
	memberships: ThinPublicMembership[]
	/** Published posts, newest first, loaded only for pages with a blog posts block. */
	blogPosts: LandingPageBlogPostSummary[]
	headerNavigationPages: LandingPageNavigationPage[]
	footerNavigationPages: LandingPageNavigationPage[]
}

export type LandingPageSite = {
	settings: LandingPageSettings
	blocks: LandingPageBlock[]
	page: {
		id: number
		path: string
		title: string
		description: string | null
		sortOrder: number
	}
	platform: PublicPlatform
	courses: ThinCourse[]
	videos: ThinVideo[]
	downloadables: ThinDownloadable[]
	sessions: ThinPrivateSession[]
	events: PublicThinEvent[]
	memberships: ThinPublicMembership[]
	/** Published posts, newest first, loaded only for pages with a blog posts block. */
	blogPosts: LandingPageBlogPostSummary[]
	headerNavigationPages: LandingPageNavigationPage[]
	footerNavigationPages: LandingPageNavigationPage[]
}

export type LandingPageBlogPostSummary = {
	id: number
	slug: string
	title: string
	metaDescription: string | null
	excerpt: string | null
	coverImageUrl: string | null
	coverImageAlt: string | null
	tags: string[]
	publishDate: Date | null
	createDate: Date
	editDate: Date | null
}

export type LandingPageBlogPost = LandingPageBlogPostSummary & {
	content: string | null
	contentHtml: string | null
}

export type LandingPageBlogArchiveMonth = {
	value: string
	count: number
}

export type ThinLandingPageBlogIndex = {
	settings: LandingPageSettings
	footerBlock: LandingPageFooterBlock
	headerNavigationPages: LandingPageNavigationPage[]
	footerNavigationPages: LandingPageNavigationPage[]
	posts: LandingPageBlogPostSummary[]
	tags: string[]
	archives: LandingPageBlogArchiveMonth[]
	filters: {
		tag: string | null
		month: string | null
		search: string | null
	}
}

export type LandingPageBlogIndex = ThinLandingPageBlogIndex & {
	platform: PublicPlatform
}

export type ThinLandingPageBlogPostPage = {
	settings: LandingPageSettings
	footerBlock: LandingPageFooterBlock
	headerNavigationPages: LandingPageNavigationPage[]
	footerNavigationPages: LandingPageNavigationPage[]
	post: LandingPageBlogPost
	relatedPosts: LandingPageBlogPostSummary[]
}

export type LandingPageBlogPostPage = ThinLandingPageBlogPostPage & {
	platform: PublicPlatform
}

export type { LandingPageBlock } from "./landingBlock"

export type LandingPageHeaderBlock = LandingPageBlockOf<"header">
export const headerNavigationDropdownThreshold = 5

export type LandingPageHeroBlock = LandingPageBlockOf<"hero">
export type LandingPageHtmlEmbedBlock = LandingPageBlockOf<"html-embed">
export type LandingPageTextBlock = LandingPageBlockOf<"text">
export type LandingPageGalleryBlock = LandingPageBlockOf<"gallery">
export type LandingPageTwoColumnsBlock = LandingPageBlockOf<"two-columns">
export type LandingPageFeaturesBlock = LandingPageBlockOf<"features">
export type LandingPageTestimonialsBlock = LandingPageBlockOf<"testimonials">
export type LandingPageFaqBlock = LandingPageBlockOf<"faq">
export type ProductListType = (typeof productListTypes)[number]
export type LandingPageProductListBlock = LandingPageBlockOf<"product-list">
export type ProductListBlockContent = LandingPageProductListBlock["content"]

export type ProductHighlightProductType = (typeof productHighlightProductTypes)[number]
export type LandingPageProductHighlightBlock = LandingPageBlockOf<"product-highlight">
export type ProductHighlightBlockContent = LandingPageProductHighlightBlock["content"]
export type BlogPostsBlockLayout = (typeof blogPostsBlockLayouts)[number]
export type LandingPageBlogPostsBlock = LandingPageBlockOf<"blog-posts">
export type BlogPostsBlockContent = LandingPageBlogPostsBlock["content"]
export type LandingPageLeadFormBlock = LandingPageBlockOf<"lead-form">
export type LandingPageFooterBlock = LandingPageBlockOf<"footer">

// BACKWARDS COMPAT: These types are kept for reference only — they are no longer part of LandingPageBlock.
// Old DB rows with these block types are converted to "product-list" by parseStoredBlock (./landingBlock.ts).
// Remove these aliases when the backwards compat loading logic is removed.
export type ProductsBlockContent = Pick<ProductListBlockContent, "title" | "productIds">
/** @deprecated Use LandingPageProductListBlock instead */
export type LandingPageCoursesBlock = { type: "courses"; content: ProductsBlockContent }
/** @deprecated Use LandingPageProductListBlock instead */
export type LandingPageVideosBlock = { type: "videos"; content: ProductsBlockContent }
/** @deprecated Use LandingPageProductListBlock instead */
export type LandingPageDownloadablesBlock = { type: "downloadables"; content: ProductsBlockContent }
/** @deprecated Use LandingPageProductListBlock instead */
export type LandingPageSessionsBlock = { type: "sessions"; content: ProductsBlockContent }
/** @deprecated Use LandingPageProductListBlock instead */
export type LandingPageEventsBlock = { type: "events"; content: ProductsBlockContent }

export function getLandingPageNavigationItems(pages: LandingPageNavigationPage[]): LandingPageNavigationItem[] {
	const items: LandingPageNavigationItem[] = []
	const groups = new Map<string, Extract<LandingPageNavigationItem, { type: "group" }>>()

	for (const page of pages) {
		const groupLabel = page.navigationGroup?.trim()
		if (!groupLabel) {
			items.push({ type: "page", page })
			continue
		}

		const existingGroup = groups.get(groupLabel)
		if (existingGroup) {
			existingGroup.pages.push(page)
			continue
		}

		const group: Extract<LandingPageNavigationItem, { type: "group" }> = {
			type: "group",
			label: groupLabel,
			pages: [page],
		}
		groups.set(groupLabel, group)
		items.push(group)
	}

	return items
}

export function hasLandingPageNavigation(pages: LandingPageNavigationPage[]): boolean {
	return pages.some(page => page.isExternal || page.path !== "/")
}

export function mergeLandingPageHeaderNavigation(
	pages: Array<Pick<LandingPageSitePage, "id" | "path" | "title" | "sortOrder" | "navigationGroup">>,
	externalLinks: LandingPageExternalNavigationLink[],
): LandingPageNavigationPage[] {
	return [
		...pages.map(page => ({
			sortOrder: page.sortOrder,
			navigationPage: {
				id: page.id,
				path: page.path,
				title: page.title,
				navigationGroup: page.navigationGroup,
			} satisfies LandingPageNavigationPage,
		})),
		...externalLinks.map(link => ({
			sortOrder: link.sortOrder,
			navigationPage: {
				id: `external-${link.id}`,
				path: link.url,
				title: link.title,
				navigationGroup: link.navigationGroup,
				isExternal: true,
			} satisfies LandingPageNavigationPage,
		})),
	]
		.sort((a, b) => a.sortOrder - b.sortOrder)
		.map(entry => entry.navigationPage)
}
